import { ArrowLongDownIcon } from '@heroicons/react/24/solid';
import { Fragment, useMemo } from 'react';
import { formatDateInTimezone } from './utils';

interface Props {
  startTime: number;
  endTime: number;
  timezone: string;
  dividerCount?: number;
  highlightTimes: number[];
}

export function TimeIndicators(props: Props) {
  const {
    dividerCount = 4,
    startTime,
    endTime,
    timezone,
    highlightTimes,
  } = props;

  const dividers = [];

  for (let i = 0; i <= dividerCount - 1; i++) {
    // Calculate percentage position (evenly distributed, including 0% and 100%)
    const position = (i / (dividerCount - 1)) * 100;
    dividers.push(position);
  }

  const highligthtPositions = useMemo(() => {
    const positions = [];
    for (let i = 0; i < highlightTimes.length; i++) {
      const time = highlightTimes[i];
      const duration = endTime - startTime;
      const position = ((time - startTime) / duration) * 100;
      positions.push(position);
    }
    return positions;
  }, [endTime, highlightTimes, startTime]);

  const getTime = (index: number) => {
    const duration = endTime - startTime;
    return startTime + (duration * index) / (dividerCount - 1);
  };

  const showTime = (index: number) => {
    if (index === 0 || index === dividers.length - 1) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <div>
      {dividers.map((position, index) => (
        <div key={index}>
          <div
            key={`${index}-divider`}
            className="pointer-events-none absolute top-0 z-10 h-full w-px opacity-80"
            style={{
              left: `${position}%`,
              backgroundImage:
                'linear-gradient(to bottom, #9CA3AF 50%, transparent 50%)',
              backgroundSize: '1px 8px',
              backgroundRepeat: 'repeat-y',
            }}
          />
          <span
            key={`${index}-text`}
            className={`text-onlight-text-3 dark:text-ondark-text-2 absolute -bottom-5 flex w-28 text-nowrap text-[11px] ${
              index === 0 ? 'justify-start' : 'justify-center'
            }`}
            style={{
              left:
                index === 0
                  ? '0'
                  : `calc(${position}% - ${
                      index === dividers.length - 1 ? '7rem' : '3.5rem'
                    })`,
            }}
          >
            {formatDateInTimezone(
              getTime(index) / 1000,
              timezone,
              showTime(index)
            )}
          </span>
        </div>
      ))}
      {highligthtPositions.map((position, index) => {
        if (position < 0 || position > 100) return null;
        return (
          <Fragment key={`${index}-highlight`}>
            <div
              className="pointer-events-none absolute -top-4 z-10"
              style={{ left: `${position}%` }}
            >
              <ArrowLongDownIcon className="text-primary-500 stroke-1.5 h-4 w-4 -translate-x-2 stroke-current" />
            </div>
            <div
              className="pointer-events-none absolute top-0 z-10 h-full w-1 opacity-80"
              style={{
                left: `${position}%`,
                backgroundImage:
                  'linear-gradient(to bottom, rgba(59, 130, 246, 0.6))',
                backgroundSize: '1px 8px',
                backgroundRepeat: 'repeat-y',
              }}
            />
          </Fragment>
        );
      })}
    </div>
  );
}

export default TimeIndicators;
